import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { IMfsDisbursementPreview, MfsFileFormat } from '../../interfaces/salaryPayment';

interface Props {
    open: boolean;
    batchId: string | null;
    format: MfsFileFormat;
    onClose: () => void;
}

const FORMAT_LABELS: Record<MfsFileFormat, string> = {
    BKASH: 'bKash',
    NAGAD: 'Nagad',
    GENERIC: 'Generic',
};

/**
 * Preview + download dialog for a batch's MFS disbursement file (spec §2, "MFS disbursement
 * file"). Invalid rows are excluded from the CSV and reported here with a reason instead of being
 * silently dropped.
 */
const MfsDisbursementPreviewDialog: React.FC<Props> = ({ open, batchId, format, onClose }) => {
    const { showApiError } = useToast();
    const [preview, setPreview] = useState<IMfsDisbursementPreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!open || !batchId) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        setPreview(null);
        PayrollService.getMfsDisbursementPreview(batchId, format)
            .then((result) => { if (!cancelled) setPreview(result); })
            .catch((error) => { if (!cancelled) showApiError(error, 'Failed to load MFS disbursement preview'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, batchId, format, showApiError]);

    const handleDownload = async () => {
        if (!batchId) return;
        setDownloading(true);
        try {
            await PayrollService.downloadMfsDisbursementCsv(batchId, format);
        } catch (error) {
            showApiError(error, 'Failed to download MFS disbursement CSV');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{FORMAT_LABELS[format]} disbursement preview</DialogTitle>
            <DialogContent dividers>
                {loading && <Typography>Loading preview...</Typography>}
                {!loading && preview && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} data-help-section="payroll.mfs.summary">
                            <Chip label={`${preview.rowCount} row(s)`} color="primary" size="small" />
                            <Chip label={`Total: ${Number(preview.totalAmount).toFixed(2)}`} size="small" />
                            {preview.excluded.length > 0 && (
                                <Chip
                                    label={`${preview.excluded.length} excluded`}
                                    color="warning"
                                    size="small"
                                />
                            )}
                        </Box>

                        <TableContainer component={Paper} variant="outlined" data-help-section="payroll.mfs.rows">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell data-help="payroll.mfs.col.employee">Employee</TableCell>
                                        <TableCell data-help="payroll.mfs.col.code">Code</TableCell>
                                        <TableCell data-help="payroll.mfs.col.provider">Provider</TableCell>
                                        <TableCell data-help="payroll.mfs.col.wallet">Wallet No</TableCell>
                                        <TableCell align="right" data-help="payroll.mfs.col.amount">Amount</TableCell>
                                        <TableCell data-help="payroll.mfs.col.reference">Reference</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {preview.rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">No valid rows</TableCell>
                                        </TableRow>
                                    )}
                                    {preview.rows.map((row) => (
                                        <TableRow key={row.paymentId}>
                                            <TableCell>{row.employeeName}</TableCell>
                                            <TableCell>{row.employeeCode}</TableCell>
                                            <TableCell>{row.provider}</TableCell>
                                            <TableCell>{row.walletNo}</TableCell>
                                            <TableCell align="right">{Number(row.amount).toFixed(2)}</TableCell>
                                            <TableCell>{row.reference}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {preview.excluded.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Excluded rows</Typography>
                                <TableContainer component={Paper} variant="outlined" data-help-section="payroll.mfs.excluded">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell data-help="payroll.mfs.exCol.employee">Employee</TableCell>
                                                <TableCell data-help="payroll.mfs.exCol.reason">Reason</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {preview.excluded.map((row) => (
                                                <TableRow key={row.paymentId}>
                                                    <TableCell>{row.employee}</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{row.reason}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.mfs.close" onClick={onClose}>Close</Button>
                <Button
                    data-help="payroll.mfs.download"
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    disabled={downloading || loading || !preview || preview.rowCount === 0}
                >
                    {downloading ? 'Downloading...' : 'Download CSV'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MfsDisbursementPreviewDialog;
